// 统一常量管理文件
// 将所有分散的常量集中管理，便于维护和复用

// ==================== 权限常量 ====================
export * from './permissions';

// ==================== 状态配置 ====================
// 链接状态配置
export const LINK_STATUS_CONFIG = {
  green: { 
    text: '绿灯', 
    description: '全部放行,不做任何拦截,一般用于自己测试',
    color: 'green'
  },
  audit: { 
    text: '审核', 
    description: '审核模式，全部拦截,一般广告过审期间使用',
    color: 'orange'
  },
  block: { 
    text: '斗篷', 
    description: '自动检测异常访客，一般用于广告正式投放',
    color: 'red'
  }
} as const;

// 链接状态类型
export type LinkStatus = keyof typeof LINK_STATUS_CONFIG;

// ==================== 规则类型配置 ====================
// 预定义的规则类型
export const RULE_TYPES = [
  { type: 'url_referer', name: '访客来源' },
  { type: 'ip_blacklist', name: 'IP黑白名单' },
  { type: 'crawler', name: '爬虫拦截' },
  { type: 'network', name: '网络安全检测' },
  { type: 'geography', name: '国家地区拦截' },
  { type: 'device', name: '设备检测' },
  { type: 'language', name: '访客语言拦截' },
  { type: 'asn', name: '网络归属控制' },
  { type: 'utm', name: 'UTM参数拦截' },
  { type: 'advertising', name: '广告平台拦截' },
] as const;

// 规则类型映射（用于显示）
export const RULE_TYPE_MAP: Record<string, { name: string; icon: string }> = {
  'url_referer': { name: '来源', icon: '🔗' },
  'ip_blacklist': { name: 'IP', icon: '🌐' },
  'crawler': { name: '爬虫', icon: '🤖' },
  'network': { name: '网络', icon: '🔐' },
  'geography': { name: '地区', icon: '🌍' },
  'device': { name: '设备', icon: '📱' },
  'language': { name: '语言', icon: '🌏' },
  'asn': { name: '网络归属', icon: '🏢' },
  'utm': { name: 'UTM', icon: '📊' },
  'advertising': { name: '广告', icon: '📢' },
};

// ==================== 用户类型配置 ====================
export const USER_TYPES = {
  ADMIN: 'admin',
  CLIENT: 'client', 
  SUB: 'sub'
} as const;

export const USER_TYPE_LABELS = {
  [USER_TYPES.ADMIN]: '系统管理员',
  [USER_TYPES.CLIENT]: '客户账号',
  [USER_TYPES.SUB]: '子账号'
} as const;

// ==================== 跳转模式配置 ====================
export const JUMP_MODES = {
  REDIRECT: 1,      // 跳转
  DISPLAY_PAGE: 2,  // 显示页面
  ORIGINAL_PAGE: 3  // 显示原始页面
} as const;

export const JUMP_BLOCK_MODES = {
  REDIRECT: 1,      // 跳转
  DISPLAY_PAGE: 2,  // 显示页面
  NOT_FOUND: 3      // 404错误
} as const;

// ==================== 访客控制配置 ====================
export const VISITOR_CONTROL_PERIODS = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month'
} as const;

export const VISITOR_CONTROL_PERIOD_LABELS = {
  [VISITOR_CONTROL_PERIODS.DAY]: '天',
  [VISITOR_CONTROL_PERIODS.WEEK]: '周',
  [VISITOR_CONTROL_PERIODS.MONTH]: '月'
} as const;

// ==================== 设备类型配置 ====================
// 注意：这些常量现在完全从服务端动态获取
// 请使用 useDeviceConstants Hook 或 deviceConstants 服务
// 不再提供静态常量作为备用方案

// ==================== 客户端类型配置 ====================
// 注意：这些常量现在完全从服务端动态获取
// 请使用 useDeviceConstants Hook 或 deviceConstants 服务
// 不再提供静态常量作为备用方案

// ==================== 网络类型配置 ====================
// 注意：这些常量现在完全从服务端动态获取
// 请使用 useDeviceConstants Hook 或 deviceConstants 服务
// 不再提供静态常量作为备用方案

// ==================== 访问状态配置 ====================
export const ACCESS_STATUS = {
  NORMAL: 'normal',
  INTERCEPTED: 'intercepted',
  ALL: 'all'
} as const;

export const ACCESS_STATUS_LABELS = {
  [ACCESS_STATUS.NORMAL]: '正常',
  [ACCESS_STATUS.INTERCEPTED]: '拦截',
  [ACCESS_STATUS.ALL]: '全部'
} as const;

// ==================== 代理状态配置 ====================
export const PROXY_STATUS = {
  HAS_PROXY: 'has_proxy',
  NO_PROXY: 'no_proxy',
  ALL: 'all'
} as const;

export const PROXY_STATUS_LABELS = {
  [PROXY_STATUS.HAS_PROXY]: '有代理',
  [PROXY_STATUS.NO_PROXY]: '无代理',
  [PROXY_STATUS.ALL]: '全部'
} as const;

// ==================== 数据中心状态配置 ====================
export const DATACENTER_STATUS = {
  YES: 'yes',
  NO: 'no',
  ALL: 'all'
} as const;

export const DATACENTER_STATUS_LABELS = {
  [DATACENTER_STATUS.YES]: '是',
  [DATACENTER_STATUS.NO]: '否',
  [DATACENTER_STATUS.ALL]: '全部'
} as const;

// ==================== 卫星网络状态配置 ====================
export const SATELLITE_STATUS = {
  YES: 'yes',
  NO: 'no',
  ALL: 'all'
} as const;

export const SATELLITE_STATUS_LABELS = {
  [SATELLITE_STATUS.YES]: '是',
  [SATELLITE_STATUS.NO]: '否',
  [SATELLITE_STATUS.ALL]: '全部'
} as const;

// ==================== IP风险等级配置 ====================
// 注意：这些常量现在完全从服务端动态获取
// 请使用 useDeviceConstants Hook 或 deviceConstants 服务
// 不再提供静态常量作为备用方案

// ==================== 分页配置 ====================
export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: ['10', '20', '50', '100'],
  SHOW_SIZE_CHANGER: true,
  SHOW_QUICK_JUMPER: true,
  SHOW_TOTAL: true
} as const;

// ==================== 搜索配置 ====================
export const SEARCH_CONFIG = {
  DEBOUNCE_DELAY: 500, // 防抖延迟（毫秒）
  MIN_SEARCH_LENGTH: 1, // 最小搜索长度
  MAX_SEARCH_LENGTH: 100 // 最大搜索长度
} as const;

// ==================== 表单配置 ====================
export const FORM_CONFIG = {
  LAYOUT: 'vertical' as const,
  REQUIRED_MARK: true,
  VALIDATE_TRIGGER: 'onChange' as const
} as const;

// ==================== 主题配置 ====================
export const THEME_CONFIG = {
  PRIMARY_COLOR: '#6366f1',
  SUCCESS_COLOR: '#52c41a',
  WARNING_COLOR: '#faad14',
  ERROR_COLOR: '#ff4d4f',
  INFO_COLOR: '#1890ff'
} as const;

// ==================== 动画配置 ====================
export const ANIMATION_CONFIG = {
  DURATION: 200, // 动画持续时间（毫秒）
  EASING: 'cubic-bezier(0.4, 0, 0.2, 1)' // 缓动函数
} as const;

// ==================== 缓存配置 ====================
export const CACHE_CONFIG = {
  PERMISSION_CACHE_TIME: 5 * 60 * 1000, // 权限缓存时间（5分钟）
  MENU_CACHE_TIME: 10 * 60 * 1000, // 菜单缓存时间（10分钟）
  USER_CACHE_TIME: 30 * 60 * 1000 // 用户信息缓存时间（30分钟）
} as const;

// ==================== API配置 ====================
export const API_CONFIG = {
  TIMEOUT: 10000, // API超时时间（10秒）
  RETRY_COUNT: 3, // 重试次数
  RETRY_DELAY: 1000 // 重试延迟（1秒）
} as const;

// ==================== 文件配置 ====================
export const FILE_CONFIG = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 最大文件大小（10MB）
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.doc', '.docx'],
  UPLOAD_PATH: '/uploads'
} as const;

// ==================== 导出配置 ====================
export const EXPORT_CONFIG = {
  MAX_EXPORT_ROWS: 10000, // 最大导出行数
  EXPORT_FORMATS: ['xlsx', 'csv', 'pdf'],
  DEFAULT_FORMAT: 'xlsx'
} as const;

// ==================== 工具函数 ====================
// 获取状态配置
export const getStatusConfig = (status: string) => {
  return LINK_STATUS_CONFIG[status as LinkStatus] || {
    text: status,
    description: '未知状态',
    color: 'default'
  };
};

// 获取规则类型信息
export const getRuleTypeInfo = (ruleType: string) => {
  return RULE_TYPE_MAP[ruleType] || { name: ruleType, icon: '📋' };
};

// 获取用户类型标签
export const getUserTypeLabel = (userType: string) => {
  return USER_TYPE_LABELS[userType as keyof typeof USER_TYPE_LABELS] || userType;
};

// 注意：以下标签函数已移除，请使用 useDeviceConstants Hook 或 deviceConstants 服务
// - getDeviceTypeLabel
// - getClientTypeLabel
// - getNetworkTypeLabel
// - getIPRiskLevelLabel

// 获取访问状态标签
export const getAccessStatusLabel = (status: string) => {
  return ACCESS_STATUS_LABELS[status as keyof typeof ACCESS_STATUS_LABELS] || status;
};

// 获取代理状态标签
export const getProxyStatusLabel = (status: string) => {
  return PROXY_STATUS_LABELS[status as keyof typeof PROXY_STATUS_LABELS] || status;
};

// 获取数据中心状态标签
export const getDatacenterStatusLabel = (status: string) => {
  return DATACENTER_STATUS_LABELS[status as keyof typeof DATACENTER_STATUS_LABELS] || status;
};

// 获取卫星网络状态标签
export const getSatelliteStatusLabel = (status: string) => {
  return SATELLITE_STATUS_LABELS[status as keyof typeof SATELLITE_STATUS_LABELS] || status;
};


// 获取访客控制周期标签
export const getVisitorControlPeriodLabel = (period: string) => {
  return VISITOR_CONTROL_PERIOD_LABELS[period as keyof typeof VISITOR_CONTROL_PERIOD_LABELS] || period;
};



   // UTM参数值现在从后端获取，不再使用静态常量


// ==================== 工具函数 ====================

